/**
 * TransferList — uTorrent-style toolbar + scrollable transfer rows.
 * Uses useTheme(), no hardcoded colors.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { TransferSession } from '@share/domain';
import { useTheme } from '../hooks/useTheme';
import { Colors, Shadows, Spacing, Radius, FontSize, FontWeight } from '../theme/tokens';
import { TransferCard } from './TransferCard';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { Icon } from '../atoms/Icon';

// ─── Types ────────────────────────────────────────────────────────────────────

type FilterMode = 'all' | 'downloading' | 'seeding' | 'completed';

export interface TransferListProps {
  sessions:        TransferSession[];
  filter?:         FilterMode;
  onPause:         (id: string) => void;
  onResume:        (id: string) => void;
  onCancel:        (id: string, deleteData?: boolean) => void;
  onAddTorrent?:   () => void;
  onAddFolder?:    () => void;
  onReceiveLink?:  () => void;
  /** Batch actions — called with all selected IDs */
  onBatchPause?:   (ids: string[]) => void;
  onBatchResume?:  (ids: string[]) => void;
  onBatchCancel?:  (ids: string[], deleteData?: boolean) => void;
}

// ─── Toolbar Chip ─────────────────────────────────────────────────────────────

interface ChipProps {
  prefix?: string;
  value:   string;
  bg:      string;
  border:  string;
  text:    string;
}

const Chip: React.FC<ChipProps> = ({ prefix, value, bg, border, text }) => (
  <View style={[chipStyles.chip, { backgroundColor: bg, borderColor: border }]}>
    {prefix && <Text style={[chipStyles.prefix, { color: text }]}>{prefix} </Text>}
    <Text style={[chipStyles.value, { color: text }]}>{value}</Text>
  </View>
);

const chipStyles = StyleSheet.create({
  chip:   { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: Radius.xs, paddingHorizontal: 10, paddingVertical: 6 },
  prefix: { fontSize: FontSize.xs, fontWeight: FontWeight.medium },
  value:  { fontSize: FontSize.xs, fontWeight: FontWeight.bold },
});

// ─── Component ────────────────────────────────────────────────────────────────

export const TransferList: React.FC<TransferListProps> = ({
  sessions,
  filter = 'all',
  onPause,
  onResume,
  onCancel,
  onAddTorrent,
  onAddFolder,
  onReceiveLink,
  onBatchPause,
  onBatchResume,
  onBatchCancel,
}) => {
  const { colors, isDark } = useTheme();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [showSendMenu, setShowSendMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Auto-clear selected items that no longer exist
  React.useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      let changed = false;
      const allIds = new Set(sessions.map(s => s.id));
      for (const id of next) {
        if (!allIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sessions]);

  const filtered = useMemo(() => sessions.filter(s => {
    if (filter === 'downloading') return s.direction === 'receive' && s.status !== 'completed';
    if (filter === 'seeding')     return s.direction === 'send' || (s.direction === 'receive' && s.status === 'completed');
    if (filter === 'completed')   return s.status === 'completed';
    return true;
  }), [sessions, filter]);

  const filteredIds = useMemo(() => filtered.map(s => s.id), [filtered]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleSelectedCount = filteredIds.filter(id => selectedIds.has(id)).length;
  const isAllSelected = visibleSelectedCount > 0 && visibleSelectedCount === filteredIds.length;
  const isIndeterminate = visibleSelectedCount > 0 && visibleSelectedCount < filteredIds.length;

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // deselect all visible
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // select all visible
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [isAllSelected, filteredIds]);

  const chipBg     = colors.surface;
  const toolbarBg  = colors.surface;
  const borderCol  = colors.border;

  return (
    <View style={[styles.container, { backgroundColor: colors.surface }]}>

      {/* uTorrent-style toolbar */}
      <View style={[styles.toolbar, { backgroundColor: toolbarBg, borderBottomColor: borderCol }]}>
        <View style={styles.toolbarLeft}>
          <TouchableOpacity 
            style={[styles.checkbox, { 
              borderColor: isAllSelected || isIndeterminate ? (colors.brand as string) : (colors.subtext as string), 
              backgroundColor: isAllSelected || isIndeterminate ? (colors.brand as string) : 'transparent',
              justifyContent: 'center', alignItems: 'center'
            }]}
            onPress={handleSelectAll}
            activeOpacity={0.8}
          >
            {isAllSelected && <Icon name="check" size={12} color={colors.surface as string} />}
            {isIndeterminate && <Icon name="close" size={10} color={colors.surface as string} />}
          </TouchableOpacity>
          {selectedIds.size > 0 && (
            <Chip value={`${selectedIds.size} selected`} bg={colors.brand as string} border={colors.brand as string} text={colors.brandText as string} />
          )}
          {selectedIds.size === 0 && (
            <>
              <Chip prefix="Show:" value="All"         bg={chipBg} border={borderCol} text={colors.text as string} />
              <Chip prefix="Sort:" value="Most Recent"  bg={chipBg} border={borderCol} text={colors.text as string} />
            </>
          )}
        </View>

        <View style={styles.toolbarRight}>
          {selectedIds.size > 0 ? (
            <View style={styles.batchActions}>
              <TouchableOpacity style={styles.batchIconBtn} onPress={() => {
                const ids = Array.from(selectedIds);
                if (onBatchPause) onBatchPause(ids); else ids.forEach(onPause);
              }}>
                <Icon name="pause" size={18} color={colors.text as string} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.batchIconBtn} onPress={() => {
                const ids = Array.from(selectedIds);
                if (onBatchResume) onBatchResume(ids); else ids.forEach(onResume);
              }}>
                <Icon name="play" size={18} color={colors.text as string} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.batchIconBtn} onPress={() => {
                setDeleteTarget({ id: 'batch', title: `${selectedIds.size} transfers` });
              }}>
                <Icon name="trash" size={18} color={colors.text as string} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.batchIconBtn} onPress={() => setSelectedIds(new Set())}>
                <Icon name="close" size={18} color={colors.text as string} />
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.toolbarActionBtn, { backgroundColor: colors.surface, borderColor: borderCol }]}
                onPress={onReceiveLink ?? onAddTorrent}
                activeOpacity={0.85}
              >
                <Text style={[styles.toolbarActionLabel, { color: colors.text as string }]}>Download +</Text>
              </TouchableOpacity>

              {/* Send + with file/folder popover */}
              <View style={dropStyles.wrap}>
                <TouchableOpacity
                  style={[styles.toolbarActionBtn, { backgroundColor: colors.brand, borderColor: colors.brand }]}
                  onPress={() => setShowSendMenu(v => !v)}
                  activeOpacity={0.85}
                >
                  <Text style={[styles.toolbarActionLabel, { color: colors.brandText as string }]}>Send +</Text>
                </TouchableOpacity>

                {showSendMenu && (
                  <View style={[dropStyles.menu, { backgroundColor: colors.surface, borderColor: borderCol }]}>
                    <TouchableOpacity
                      style={dropStyles.item}
                      onPress={() => { setShowSendMenu(false); onAddTorrent?.(); }}
                      activeOpacity={0.8}
                    >
                      <Icon name="file" size={14} color={colors.text as string} />
                      <Text style={[dropStyles.label, { color: colors.text as string }]}>Send Files</Text>
                    </TouchableOpacity>
                    <View style={[dropStyles.divider, { backgroundColor: borderCol }]} />
                    <TouchableOpacity
                      style={dropStyles.item}
                      onPress={() => { setShowSendMenu(false); onAddFolder?.(); }}
                      activeOpacity={0.8}
                    >
                      <Icon name="folder" size={14} color={colors.text as string} />
                      <Text style={[dropStyles.label, { color: colors.text as string }]}>Send Folder</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </>
          )}
        </View>
      </View>

      {/* Transfer rows */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          filtered.length === 0 && styles.emptyScrollContent,
        ]}
        nestedScrollEnabled
      >
        {filtered.length === 0 ? (
          <EmptyState borderColor={borderCol} textColor={colors.text} subtextColor={colors.subtext} />
        ) : (
          filtered.map((s, i) => (
            <TransferCard
              key={s.id}
              index={i}
              session={s}
              onPause={onPause}
              onResume={onResume}
              onCancel={(id: string, title?: string) => setDeleteTarget({ id, title: title || 'this transfer' })}
              selected={selectedIds.has(s.id)}
              onToggleSelect={toggleSelect}
            />
          ))
        )}
      </ScrollView>

      {/* Delete confirmation sub-popup */}
      <ConfirmDeleteModal
        visible={!!deleteTarget}
        itemTitle={deleteTarget?.title}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(deleteData: boolean) => {
          if (deleteTarget) {
            const id = deleteTarget.id;
            setDeleteTarget(null); // clear first to avoid stale modal state
            if (id === 'batch') {
              const ids = Array.from(selectedIds);
              if (onBatchCancel) onBatchCancel(ids, deleteData); else ids.forEach(uid => onCancel(uid, deleteData));
              setSelectedIds(new Set());
            } else {
              onCancel(id, deleteData);
            }
          }
        }}
      />
    </View>
  );
};

// ─── EmptyState ───────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ borderColor: string; textColor: string; subtextColor: string }> = ({
  borderColor, textColor, subtextColor
}) => (
  <View style={esStyles.wrap}>
    <View style={[esStyles.dropZone, { borderColor }]}>
      <Icon name="upload" size={20} color={textColor} />
      <Text style={[esStyles.label, { color: textColor }]}>Drag and drop a file.</Text>
    </View>
    <Text style={[esStyles.sub, { color: subtextColor }]}>
      Select files or join a link to start sharing
    </Text>
  </View>
);

const esStyles = StyleSheet.create({
  wrap:     { alignItems: 'center', justifyContent: 'center', flex: 1, paddingVertical: Spacing.md },
  dropZone: { flexDirection: 'row', alignItems: 'center', borderWidth: 1.5, borderStyle: 'dashed', borderRadius: Radius.md, paddingVertical: 18, paddingHorizontal: 28, marginBottom: Spacing.md, gap: 10 } as any,
  cursor:   { fontSize: 22, opacity: 0.5 },
  label:    { fontSize: FontSize.md, fontWeight: FontWeight.semibold },
  sub:      { fontSize: FontSize.sm, textAlign: 'center' },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex:         1,
    borderRadius: Radius.lg, // 12px
    // overflow: 'hidden' removed — clips the Send+ dropdown.
    // Shadow and radius still render correctly without it on web.
    ...Shadows.card,
  },
  toolbar: {
    flexDirection:    'row',
    alignItems:       'center',
    justifyContent:   'space-between',
    paddingHorizontal: Spacing.md,
    paddingVertical:  Spacing.md,
    borderBottomWidth: 1,
    zIndex:           10,  // ensure toolbar sits above scroll content
  },
  toolbarLeft:  { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm } as any,
  toolbarRight: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm } as any,
  checkbox: {
    width: 16, height: 16,
    borderWidth: 1.5,
    borderRadius: Radius.sm,
  },
  toolbarActionBtn: {
    height:            36,
    flexDirection:     'row',
    alignItems:        'center',
    borderRadius:      Radius.pill,
    paddingHorizontal: 16,
    borderWidth:       1,
  },
  toolbarActionLabel: {
    fontSize:   14,
    fontWeight: FontWeight.bold,
  },
  scroll:             { flex: 1 },
  scrollContent:      { padding: Spacing.md, paddingBottom: 24 },
  emptyScrollContent: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  batchActions: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm } as any,
  batchIconBtn: {
    width: 36, height: 36,
    borderRadius: Radius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const dropStyles = StyleSheet.create({
  wrap: {
    position: 'relative' as any,
  },
  menu: {
    position:     'absolute' as any,
    bottom:       44,   // open upward — above the button, never behind list rows
    right:        0,
    width:        150,
    borderWidth:  1,
    borderRadius: Radius.md,
    zIndex:       999,
    ...Shadows.card,
  } as any,
  item: {
    flexDirection:     'row',
    alignItems:        'center',
    paddingVertical:   10,
    paddingHorizontal: 14,
    gap:               8,
  } as any,
  label: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.medium,
  },
  divider: {
    height: 1,
  },
});
